import { useMemo, useState } from "react";
import { useConvex } from "convex/react";
import { ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import { api as convexApi } from "../../../lib/convexApi";

// The audit hash chain is what makes the log tamper-EVIDENT rather than merely
// hashed, and it had no reader in the UI at all: verifyAuditChain was callable
// only by the daily cron, and when that cron found a break it wrote an ordinary
// audit row (type "audit.chain.integrity-alert") into the same feed as
// everything else — one grey line among thousands, with no distinct rendering.
// An alarm nobody can find is not an alarm. This banner is both halves: it
// raises the cron's finding to the top of the page, and it makes the on-demand
// verifier reachable.

// The event type recordIntegrityFinding writes (convex/audit/integritySweep.ts).
export const INTEGRITY_ALERT_TYPE = "audit.chain.integrity-alert";

// One interactive check reads at most this many pages. The chain can outgrow a
// single query's document-read budget, so verifyAuditChain pages via resumeSeq;
// without a bound a malformed cursor would spin forever. On exhaustion we say
// what was actually covered rather than claiming a clean result.
const MAX_PAGES = 40;
const PAGE_LIMIT = 1000;

type VerifyPage = {
  intact: boolean;
  reason?: string;
  checked?: number;
  chained?: boolean;
  brokenAt?: string;
  resumeSeq?: number;
};

type State =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "intact"; checked: number; chained: boolean }
  | { kind: "broken"; reason: string; brokenAt?: string }
  | { kind: "partial"; checked: number }
  | { kind: "error"; message: string };

interface AuditRowish {
  type?: unknown;
  recordedAt?: unknown;
  detail?: unknown;
}

function asString(v: unknown): string | undefined {
  return typeof v === "string" && v ? v : undefined;
}

// A normalized row's `recordedAt` is an ISO string (auditNormalize `isoFrom`),
// but this banner renders the one row an operator must be able to trust, and a
// literal "Invalid Date" next to a tamper finding reads as the ALERT being
// broken rather than the timestamp. Drop the clause instead — the alert itself
// is the message; the timestamp is context.
function asWhen(v: unknown): string | undefined {
  const s = asString(v);
  if (!s) return undefined;
  const t = new Date(s);
  return Number.isNaN(t.getTime()) ? undefined : t.toLocaleString();
}

export function ChainIntegrityBanner({ rows }: { rows: AuditRowish[] }) {
  const convex = useConvex();
  const [state, setState] = useState<State>({ kind: "idle" });

  // The cron's own finding, if one is in the rows the page already loaded. No
  // extra query — the alert is an audit event like any other, it just has to
  // stop looking like one.
  const alert = useMemo(() => {
    // `r?.type`: this banner renders above a feed of arbitrary audit rows, and
    // one malformed row must not be able to suppress a tamper alert by
    // crashing the component that reports it.
    const hit = rows.find((r) => asString(r?.type) === INTEGRITY_ALERT_TYPE);
    if (!hit) return null;
    // `detail` singular: this component receives NORMALIZED rows, and
    // normalizeAuditRow folds the raw Convex `details` into `detail`
    // (auditNormalize.ts:187-189). Reading `details` here would find nothing.
    const detail = (hit.detail ?? {}) as Record<string, unknown>;
    return {
      at: asWhen(hit.recordedAt),
      reason: asString(detail.reason) ?? "unknown",
      brokenAt: asString(detail.brokenAt),
    };
  }, [rows]);

  async function verify() {
    setState({ kind: "running" });
    let cursor: number | undefined = undefined;
    let checked = 0;
    try {
      for (let page = 0; page < MAX_PAGES; page++) {
        const r: VerifyPage = await convex.query((convexApi as any).audit.verify.verifyAuditChain, {
          limit: PAGE_LIMIT,
          ...(cursor === undefined ? {} : { fromSeq: cursor }),
        });
        checked += r.checked ?? 0;
        if (r.intact) {
          setState({ kind: "intact", checked, chained: r.chained !== false });
          return;
        }
        // "incomplete" is explicitly NOT a tamper reason — it means this page
        // ran out of budget before reaching the head. Anything else is.
        if (r.reason !== "incomplete") {
          setState({ kind: "broken", reason: r.reason ?? "unknown", brokenAt: r.brokenAt });
          return;
        }
        // Resume strictly from the page's own cursor. Resuming from an
        // arbitrary seq would certify a range this walk never read.
        if (typeof r.resumeSeq !== "number") {
          setState({ kind: "partial", checked });
          return;
        }
        cursor = r.resumeSeq;
      }
      setState({ kind: "partial", checked });
    } catch (e) {
      setState({ kind: "error", message: e instanceof Error ? e.message : "Verification failed" });
    }
  }

  const running = state.kind === "running";

  return (
    <div className="sr-chain-strip">
      {alert && (
        <div className="sr-chain-alert" role="alert">
          <ShieldAlert size={16} aria-hidden="true" />
          <span>
            <strong>Audit chain integrity alert</strong> — the daily sweep reported{" "}
            <code>{alert.reason}</code>
            {alert.brokenAt ? (
              <>
                {" "}
                at event <code>{alert.brokenAt}</code>
              </>
            ) : null}
            {alert.at ? <> on {alert.at}</> : null}. The append-only log can no longer be certified
            intact from that point.
          </span>
        </div>
      )}
      <div className="sr-chain-check">
        <button
          type="button"
          className="sr-btn sr-btn--ghost"
          onClick={() => void verify()}
          disabled={running}
        >
          <ShieldCheck size={14} aria-hidden="true" /> {running ? "Verifying…" : "Verify chain"}
        </button>
        {state.kind === "intact" && (
          <span className="sr-chain-result is-ok" role="status">
            {state.chained
              ? `Chain intact — ${state.checked.toLocaleString()} events verified`
              : "No chained events yet — nothing to verify"}
          </span>
        )}
        {state.kind === "broken" && (
          <span className="sr-chain-result is-bad" role="alert">
            <ShieldAlert size={14} aria-hidden="true" /> Chain BROKEN — {state.reason}
            {state.brokenAt ? ` at ${state.brokenAt}` : ""}
          </span>
        )}
        {state.kind === "partial" && (
          <span className="sr-chain-result is-warn" role="status">
            <ShieldQuestion size={14} aria-hidden="true" /> Verified the most recent{" "}
            {state.checked.toLocaleString()} events — the chain is longer than one interactive
            check. The daily sweep covers the whole chain.
          </span>
        )}
        {state.kind === "error" && (
          <span className="sr-chain-result is-bad" role="alert">
            {state.message}
          </span>
        )}
      </div>
    </div>
  );
}
